import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { ImageListModal, ImageModal } from '@/shared/components/ui/Images';
import {
  EstadosImagenChoiceEnum,
  calcOTImages,
  solicitudUpdFotosSchema,
} from '@/shared';
import {
  OkFotosSalidasUpddActivacion,
  UpdFotosSalidaRequestData,
  useUpdateOrdenTrabajo,
} from '@/shared/actions/erp';
import { TabTextLabel } from '@/shared/components/ui';
import { ScrollableDialogProps } from '@/shared/components/ui/Dialogs';
import { OrdenTrabajo } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import ConfirmUpdFotosAlertActivacionFotosModal from './ConfirmUpdFotosAlertActivacionFotosModal';

export type FotosSalidaUpdatedReviewModalProps = {
  ordenTrabajo: OrdenTrabajo;
  open: boolean;
  onClose: () => void;
};

type FormData = {
  comentario_solicita_upd_fotos: string;
  fecha_solicita_upd_fotos: string;
  usuario_solicita_upd_fotos: number;
};

const FotosSalidaUpdatedReviewModal: React.FC<
  FotosSalidaUpdatedReviewModalProps
> = ({ ordenTrabajo, onClose, open }) => {
  ///* local state ------------
  const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false);
  const [imagesArr, setImagesArr] = useState<ImageModal[]>([]);

  ///* global state ------------
  const setIsGlobalLoading = useUiStore(s => s.setIsGlobalLoading);

  ///* hooks ------------

  ///* form ------------
  const form = useForm<FormData>({
    resolver: yupResolver(solicitudUpdFotosSchema) as any,
  });

  ///* mutations ------------
  const updOtOkUpdatedFotos =
    useUpdateOrdenTrabajo<OkFotosSalidasUpddActivacion>({
      enableRedirect: false,
      customSuccessMessage: 'Fotos de Salida Aprbadas con éxito',
    });
  const updOtFotosEntrada = useUpdateOrdenTrabajo<UpdFotosSalidaRequestData>({
    enableRedirect: false,
    customSuccessMessage:
      'Solicitud de actualización de fotos enviada con éxito',
  });

  ///* handlers ------------
  const handleClose = () => {
    onClose();
  };

  const handleConfirm = async () => {
    setIsGlobalLoading(true);
    handleClose();

    updOtOkUpdatedFotos.mutate({
      id: ordenTrabajo?.id_orden_trabajo!,
      data: {
        estado_imagen_actualizacion: EstadosImagenChoiceEnum.UPD_APROBADA,
        estado_imagen_salida: EstadosImagenChoiceEnum.CARGADO,
        estado_imagen_entrada: EstadosImagenChoiceEnum.CARGADO,
      },
    });

    setIsGlobalLoading(true);
  };

  const handleRequestUpd = async (data: FormData) => {
    updOtFotosEntrada.mutate({
      id: ordenTrabajo?.id_orden_trabajo!,
      data: {
        estado_imagen_salida: EstadosImagenChoiceEnum.UPD_SOLICITADA,
        estado_imagen_actualizacion: EstadosImagenChoiceEnum.UPD_SOLICITADA,
        observaciones: {
          ...ordenTrabajo?.observaciones,
          observacion_foto_salida_upd_sol: data?.comentario_solicita_upd_fotos,
        },
      },
    });
  };

  ///* effects ------------
  useEffect(() => {
    if (!open) return;

    // antes era async
    const photesEntrada = calcOTImages(ordenTrabajo, 'urls_fotos_salida');
    setImagesArr(photesEntrada);
  }, [open]);

  return (
    <>
      <ScrollableDialogProps
        open={open}
        title="Fotos de Salida Actualizadas"
        minWidth="54%"
        minHeight="30vh"
        contentNode={
          <>
            <Grid item xs={12} container spacing={3}>
              <>
                {/* ----- Image Modal ----- */}
                <Grid item xs={12}>
                  <TabTextLabel text="Fotos de Salida" pt="0" pl="0" pb="0" />
                </Grid>
                <ImageListModal images={imagesArr || []} />
              </>
            </Grid>
          </>
        }
        onClose={handleClose}
        showCustomButtons
        customButtons={
          <>
            <Grid
              item
              container
              xs={12}
              alignItems="center"
              justifyContent="flex-end"
            >
              <Button
                onClick={() => {
                  handleClose();
                }}
                variant="text"
                color="inherit"
              >
                Cerrar
              </Button>
              <span className="spacer" />

              {/* ---------- REJECT ---------- */}
              <Button
                onClick={() => {
                  setIsOpenConfirmDialog(true);
                }}
                color="error"
              >
                Solicitar Actualización de Fotos
              </Button>

              {/* ---------- CONFIRM ---------- */}
              <Button
                onClick={() => {
                  handleConfirm();
                }}
                variant="outlined"
                color="primary"
              >
                Validar Actualización
              </Button>
            </Grid>
          </>
        }
      />

      {/* ========= confirm dialog ========= */}
      <ConfirmUpdFotosAlertActivacionFotosModal
        form={form}
        handleConfirm={handleRequestUpd}
        isOpenConfirmDialog={isOpenConfirmDialog}
        setIsOpenConfirmDialog={setIsOpenConfirmDialog}
        isSalida
      />
    </>
  );
};

export default FotosSalidaUpdatedReviewModal;
